/**
 * Bundle Rewards cart-watcher — free gift-with-purchase tiers.
 *
 * Site-wide. Counts the cart's KITS (Single-Kit units + pre-made bundle components via
 * window.BBR_BUNDLE_KITS) and keeps the free gifts in sync: each tier { variantId,
 * threshold } is auto-added at/above its threshold and removed below it. Same kit signal
 * as the discount + free-shipping fn.
 *
 * Robustness guarantees (it must never "add random shit"):
 *   - Only ever touches lines IT added (tagged properties._auto_gift=1). A shopper's own
 *     purchased Tool Kit / Mystery Pack is never removed.
 *   - Auto-gift lines are NOT counted toward the kit total (no self-reinforcing loops).
 *   - After adding, the gift MUST be free: if no discount zeroes it (final_line_price>0)
 *     it's pulled back out, so a shopper is never charged for a "free" gift.
 *   - One mutation per pass, busy-guarded; failed adds (e.g. sold-out) give up after
 *     MAX_TRIES instead of looping.
 *
 * Config (layout/theme.liquid):
 *   window.BBR_GIFTS = [{ variantId, threshold }, ...]   (e.g. Tool @6, Mystery Pack @6)
 *   window.BBR_GIFT_KIT_TYPE = 'Single Kit'
 *   window.BBR_BUNDLE_KITS  = { <productId>: <kitCount> }  (pre-made bundles)
 * (Back-compat: window.BBR_GIFT = { variantId, threshold, kitType } single gift.)
 */
(function () {
  'use strict';

  var GIFTS = (window.BBR_GIFTS && window.BBR_GIFTS.length)
    ? window.BBR_GIFTS
    : (window.BBR_GIFT ? [window.BBR_GIFT] : []);
  if (!GIFTS.length) return;
  GIFTS = GIFTS
    .map(function (g) { return { id: String(g.variantId), threshold: g.threshold || 0 }; })
    .filter(function (g) { return g.id && g.id !== 'null'; });
  if (!GIFTS.length) return;

  var KIT_TYPE = window.BBR_GIFT_KIT_TYPE || (window.BBR_GIFT && window.BBR_GIFT.kitType) || 'Single Kit';
  var GIFT_IDS = GIFTS.map(function (g) { return g.id; });

  var busy = false;
  function hold() { busy = true; setTimeout(function () { busy = false; evaluate(); }, 1600); }

  // Failure backoff: if a gift add doesn't "take" (e.g. the variant is sold out or
  // otherwise can't be added), give up after a couple of tries instead of re-adding —
  // and re-opening the drawer — on every cart change. This is what stopped the cart
  // "reloading like crazy".
  var attempts = {}; // variantId -> consecutive failed add attempts
  var skip = {};     // variantId -> true once we've given up this session
  var MAX_TRIES = 2;

  function evaluate() {
    if (busy) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var bundleKits = window.BBR_BUNDLE_KITS || {};
        var kit = 0;
        var giftLine = {}; // variantId -> the gift line WE auto-added (never user-added items)
        items.forEach(function (it) {
          var auto = it.properties && String(it.properties._auto_gift) === '1';
          if (auto) {
            // our own gift line: track it, and never count it toward the kit total
            if (GIFT_IDS.indexOf(String(it.variant_id)) !== -1) giftLine[String(it.variant_id)] = it;
            return;
          }
          if (it.product_type === KIT_TYPE) kit += it.quantity;
          else if (bundleKits[it.product_id]) kit += bundleKits[it.product_id] * it.quantity; // pre-made bundle = N kits
        });
        // one mutation per pass; the resulting cart change re-runs evaluate for the next tier
        for (var i = 0; i < GIFTS.length; i++) {
          var g = GIFTS[i];
          if (skip[g.id]) continue;
          var line = giftLine[g.id];
          if (kit >= g.threshold) {
            if (!line) {
              // qualified, but our gift isn't in the cart yet — add it (bounded retries)
              attempts[g.id] = (attempts[g.id] || 0) + 1;
              if (attempts[g.id] > MAX_TRIES) { skip[g.id] = true; continue; }
              hold(); addGift(g.id); return;
            }
            // present + qualified: it MUST actually be free. If no discount is zeroing it
            // out, pull it so a shopper is never charged for a "free" gift — and stop.
            if ((line.final_line_price || 0) > 0) { skip[g.id] = true; hold(); removeGift(line); return; }
            attempts[g.id] = 0; // confirmed free + earned
          } else if (line) {
            // no longer qualified — remove the gift WE added (only ever our own line)
            attempts[g.id] = 0; hold(); removeGift(line); return;
          }
        }
      })
      .catch(function () {});
  }

  function addGift(variantId) {
    var url = (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
    // Add SILENTLY. We deliberately do not route through `theme:cart:add` — that
    // surfaces add errors (e.g. a sold-out gift -> "No more products available")
    // as a banner in the cart drawer. A free bonus should never show the shopper
    // an error; if it can't be added, the failure backoff just stops trying.
    fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties: { _auto_gift: '1' } })
    })
      .then(function (r) {
        return r.json().then(function (res) { return { ok: r.ok, res: res }; }, function () { return { ok: r.ok, res: {} }; });
      })
      .then(function (o) {
        if (!o.ok || (o.res && o.res.status)) return; // couldn't add — stay quiet, backoff handles it
        // keep the cart-count badge in sync (theme has no drawer-reload event)
        fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true, detail: { cartCount: cart.item_count } }));
          })
          .catch(function () {});
      })
      .catch(function () {});
  }

  function removeGift(line) {
    var el = document.querySelector('[data-id="' + line.key + '"]');
    if (el) {
      el.dispatchEvent(new CustomEvent('theme:cart:update', { detail: { id: line.key, quantity: 0 }, bubbles: true }));
    } else {
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 })
      }).catch(function () {});
    }
  }

  ['theme:cart:change', 'theme:product:added', 'theme:cart:update'].forEach(function (ev) {
    document.addEventListener(ev, function () { setTimeout(evaluate, 400); });
  });
  if (document.readyState !== 'loading') setTimeout(evaluate, 700);
  else document.addEventListener('DOMContentLoaded', function () { setTimeout(evaluate, 700); });
})();
